import React from 'react';
import {
  Platform,
  StyleSheet,
  Text,
  View,
  useWindowDimensions,
} from 'react-native';

import { PrintPDFDropdown } from './forms';
import { ProcessingBadge } from './shared/ProcessingBadge';
import { StageBadge } from './StageBadge';
import { useResponsive } from '../hooks/useResponsive';
import { colors } from '../theme/colors';
import { spacing } from '../theme/spacing';

import type { RequestStage } from '../types/stages';

export interface RequestToolbarProps {
  title: string;
  /**
   * Request number (#1531) — the bare id; this component owns the `Request #…`
   * wording so the two layout branches cannot word it differently.
   *
   * It renders as a second identity line UNDER the title, never beside it: the
   * title row is the one place on this toolbar that is already short of width
   * (it truncates at 1024px on web and on every phone), and the actions are
   * what the reporter called crowded. A line of its own costs the title
   * nothing, and on wide web it costs no height either — `leftStack` is
   * already a column and the band's 64px `minHeight` absorbs the second line.
   *
   * Request Detail passes it; Create Request does not (a request being created
   * has no number yet).
   */
  requestNumber?: string | null;
  stage?: RequestStage | string | null;
  /** Raw request stage before normalizeStage — needed for PROCESSING badge parity. */
  rawStage?: string | null;
  formData?: Record<string, unknown>;
  /** Stable ref for live PDF payload without re-registering toolbar chrome. */
  formDataRef?: React.RefObject<Record<string, unknown> | undefined>;
  titleAccessory?: React.ReactNode;
  /**
   * Location Tags (#999).
   * Compact/native Chapman: row 2 leading. Wide web: after stage on the title row.
   */
  actionLeading?: React.ReactNode;
  /**
   * View Devices (#999), and since #1069 Version History and Discussion too.
   * Wide web: after PDF on the actions cluster. Compact/native: row 2, after
   * PDF, in a wrapping right-aligned group (it was row 1 trailing until
   * 2026-09-09: three labelled pills beside the title forced row 1 to wrap
   * and broke the stage badge across lines on a phone).
   *
   * ⚠️ Pass the buttons DIRECTLY (a fragment), not inside your own row View.
   * Both branches own the slot's row: the compact group wraps between its
   * children so a 320pt device drops the last button to a new line instead
   * of clipping, and the wide wrapper lays them out beside PDF. A caller's
   * own View is one child on either branch and defeats both.
   */
  actionTrailing?: React.ReactNode;
  isStageUpdating?: boolean;
  /**
   * Internal-only "Configuration" label on the stage badge (PRD FR7). This component is
   * mounted headlessly through RequestFrameContext and has no user in scope, so the value
   * has to come from the screen that supplies `stage` (RequestDetail / CreateRequest).
   */
  showInternalDetail?: boolean;
  disabled?: boolean;
  testID?: string;
}

export const REQUEST_TOOLBAR_WEB_HEIGHT = 64;
export const REQUEST_TOOLBAR_COMPACT_MAX_WIDTH = 640;

function ToolbarVerticalDivider({ testID }: { testID?: string }) {
  return (
    <View
      style={styles.toolbarDivider}
      testID={testID}
      accessibilityElementsHidden
      importantForAccessibility="no-hide-descendants"
    />
  );
}

/**
 * Compact = native always + narrow web.
 * Only compact uses Chapman two-row chrome (UX-DR7). Wide web keeps the single-band toolbar.
 */
function useCompactRequestToolbar(): boolean {
  const { width } = useWindowDimensions();
  const { isSmallScreen } = useResponsive();
  if (Platform.OS !== 'web') {
    return true;
  }
  return isSmallScreen || width <= REQUEST_TOOLBAR_COMPACT_MAX_WIDTH;
}

export const RequestToolbar: React.FC<RequestToolbarProps> = ({
  title,
  requestNumber,
  stage,
  rawStage,
  formData,
  formDataRef,
  titleAccessory,
  actionLeading,
  actionTrailing,
  isStageUpdating = false,
  showInternalDetail = false,
  disabled = false,
  testID = 'request-toolbar',
}) => {
  const isCompact = useCompactRequestToolbar();
  const isProcessingStage = rawStage === 'PROCESSING';
  const showStageCluster = stage != null || isProcessingStage;

  // One node, rendered in both branches, so web and native cannot drift into
  // saying the request number differently. `selectable` is the whole copy
  // affordance: a requestor quoting the number into an email or a ticket can
  // take it from here (web drag-select, iOS long-press) without the toolbar
  // growing another button.
  const requestNumberLine = requestNumber ? (
    <Text
      testID="request-toolbar-request-number"
      style={styles.requestNumber}
      numberOfLines={1}
      selectable
      accessibilityLabel={`Request number ${requestNumber}`}
    >
      {`Request #${requestNumber}`}
    </Text>
  ) : null;

  const stageCluster = showStageCluster ? (
    <View style={styles.stageRow}>
      {isProcessingStage ? (
        <ProcessingBadge stage="PROCESSING" size="small" />
      ) : null}
      {stage != null ? (
        <StageBadge
          testID="request-toolbar-stage"
          stage={stage}
          size="small"
          isUpdating={isStageUpdating}
          showInternalDetail={showInternalDetail}
        />
      ) : null}
    </View>
  ) : null;

  // Wide web — preserve prior single-band chrome (title|stage|Tags · PDF|Devices).
  if (!isCompact) {
    const actionLeadingCluster = actionLeading ? (
      <View
        style={styles.actionLeadingCluster}
        testID={`${testID}-action-leading`}
      >
        <ToolbarVerticalDivider testID={`${testID}-action-leading-divider`} />
        {actionLeading}
      </View>
    ) : null;

    // The identity row and the actions are siblings inside ONE row, and the
    // number hangs below that row (#1531 review). The obvious shape — number
    // as a second child of `leftStack` — made the left block two lines tall
    // while the actions stayed centred in the 64px band, so the stage badge
    // rode ~10px above PDF and the trailing buttons. Keeping them in a shared
    // `alignItems: 'center'` row means the whole line moves together and the
    // badge never leaves the buttons' centre line. The title row must also
    // stay a SIBLING of the actions rather than growing to full width: the E2E
    // chrome spec asserts `titleRow.right <= actions.left + 1`.
    return (
      <View style={styles.shell} testID={testID}>
        <View style={styles.contentWide} testID={`${testID}-content`}>
          <View style={styles.wideBandRow} testID={`${testID}-band-row`}>
            <View style={styles.leftStack} testID={`${testID}-left-stack`}>
              <View style={styles.titleRow} testID={`${testID}-title-row`}>
                <Text
                  testID="request-toolbar-title"
                  style={styles.title}
                  numberOfLines={1}
                  accessibilityRole="header"
                >
                  {title}
                </Text>
                {titleAccessory ? (
                  <View style={styles.titleAccessory}>{titleAccessory}</View>
                ) : null}
                {stageCluster}
                {actionLeadingCluster}
              </View>
            </View>
            <View style={styles.actionCluster} testID={`${testID}-actions`}>
              <PrintPDFDropdown
                formData={formData}
                formDataRef={formDataRef}
                disabled={disabled}
              />
              {actionTrailing ? (
                <View
                  style={styles.actionTrailingWide}
                  testID={`${testID}-action-trailing`}
                >
                  {actionTrailing}
                </View>
              ) : null}
            </View>
          </View>
          {requestNumberLine}
        </View>
      </View>
    );
  }

  // Compact / native — Chapman two-row (UX-DR7). Compact is native always
  // and web under `isSmallScreen` (1024px), not only under 640. Row 1 is
  // identity only and never wraps: the title truncates and the stage badge
  // stays whole. Row 2 carries every action: Location Tags leading, then
  // PDF, then the trailing group. The row is end-justified and Tags holds
  // the left edge with an auto RIGHT margin; that is the only auto margin in
  // the row, on purpose. Yoga counts the auto margin of the item that breaks
  // a line into the line it did NOT fit on (FlexLine.cpp), so an auto left
  // margin on both PDF and the group put PDF at the midpoint of line one on
  // every phone and orphaned it mid-row on a tablet (measured in review,
  // 2026-09-09). The row wraps only where it must: one line on compact web
  // or a tablet wide enough to hold everything; on a phone Tags and PDF
  // share line one and the trailing group takes line two; where the group
  // is wider than the line (320pt native, 390px web with the larger web
  // pills) it wraps its own buttons once more rather than clip. Three lines
  // is the floor with every label visible at phone widths (owner decision,
  // 2026-09-09).
  return (
    <View style={[styles.shell, styles.shellCompact]} testID={testID}>
      <View
        style={[styles.content, styles.contentCompact]}
        testID={`${testID}-content`}
      >
        <View
          style={[
            styles.bandRow,
            styles.bandRowCompact,
            styles.bandRowIdentity,
            styles.bandRowCompactDivider,
          ]}
          testID={`${testID}-row-1`}
        >
          <View
            style={[styles.identityStack, styles.identityStackCompact]}
            testID={`${testID}-identity-stack`}
          >
            <View style={styles.identityInline} testID={`${testID}-identity`}>
              <Text
                testID="request-toolbar-title"
                style={styles.title}
                numberOfLines={1}
                accessibilityRole="header"
              >
                {title}
              </Text>
              {titleAccessory ? (
                <View style={styles.titleAccessory}>{titleAccessory}</View>
              ) : null}
              {stageCluster}
            </View>
            {requestNumberLine}
          </View>
        </View>

        <View
          style={[styles.bandRow, styles.bandRowCompact, styles.bandRowActions]}
          testID={`${testID}-row-2`}
        >
          {actionLeading ? (
            <View
              style={[styles.actionLeadingCluster, styles.actionLeadingCompact]}
              testID={`${testID}-action-leading`}
            >
              {actionLeading}
            </View>
          ) : null}
          <View
            style={[styles.actionCluster, styles.actionClusterCompact]}
            testID={`${testID}-actions`}
          >
            <PrintPDFDropdown
              formData={formData}
              formDataRef={formDataRef}
              disabled={disabled}
            />
          </View>
          {actionTrailing ? (
            <View
              style={styles.actionTrailingCompact}
              testID={`${testID}-action-trailing`}
            >
              {actionTrailing}
            </View>
          ) : null}
        </View>
      </View>
    </View>
  );
};

/** Compact row inset — equal above/below controls inside each Chapman band. */
export const REQUEST_TOOLBAR_COMPACT_ROW_PADDING_Y = 8;

const styles = StyleSheet.create({
  shell: {
    backgroundColor: colors.surfaceRequestToolbar,
    borderTopWidth: 1,
    borderTopColor: colors.borderCard,
    borderBottomWidth: 1,
    borderBottomColor: colors.borderCard,
    width: '100%',
    // Never let the flex column below steal this chrome (empty hairline bug).
    flexShrink: 0,
    ...Platform.select({
      web: {
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: REQUEST_TOOLBAR_WEB_HEIGHT,
      },
      default: {
        alignItems: 'stretch',
        justifyContent: 'flex-start',
      },
    }),
  },
  shellCompact: {
    // Height from padded rows — avoid minHeight air that skewed insets.
    minHeight: 0,
    justifyContent: 'flex-start',
  },
  // Wide web is a COLUMN since #1531: the band row (identity + actions) and the
  // request number line under it. The column centres that pair inside the 64px
  // band, so the row keeps its own internal alignment whether or not the number
  // is there — the identity and the actions rise together or not at all.
  // `gap` is the title-to-number step, NOT the identity-to-actions one; that
  // one lives on `wideBandRow` along with `justifyContent: 'space-between'`.
  contentWide: {
    width: '100%',
    alignSelf: 'stretch',
    flexDirection: 'column',
    alignItems: 'stretch',
    justifyContent: 'center',
    gap: 4,
    ...Platform.select({
      web: {
        paddingHorizontal: 20,
        height: '100%' as const,
        minHeight: REQUEST_TOOLBAR_WEB_HEIGHT,
      },
      default: {
        paddingHorizontal: 12,
        paddingVertical: 6,
      },
    }),
  },
  // The one line the stage badge and the action buttons share. `alignItems:
  // 'center'` here is what pins them to a common centre; it is the whole fix
  // for the ~10px badge rise found in review.
  wideBandRow: {
    width: '100%',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.md,
    minWidth: 0,
  },
  content: {
    width: '100%',
    alignSelf: 'stretch',
    flexDirection: 'column',
    alignItems: 'stretch',
    justifyContent: 'flex-start',
    gap: spacing.sm,
    ...Platform.select({
      web: {
        paddingHorizontal: 20,
        paddingVertical: spacing.sm,
        minHeight: 0,
      },
      default: {
        paddingHorizontal: 12,
        paddingVertical: 6,
      },
    }),
  },
  contentCompact: {
    // Vertical inset lives on each band row so top/bottom stay equal.
    gap: 0,
    minHeight: 0,
    paddingVertical: 0,
  },
  // Holds the title row only. It stays a column (and keeps its name) so the
  // `maxWidth: '100%'` truncation contract on `titleRow` below still resolves
  // against a constrained parent. `height: '100%'` was dropped with #1531:
  // inside `wideBandRow` it resolved against an auto height and the row's
  // `alignItems: 'center'` now owns vertical placement.
  leftStack: {
    flexDirection: 'column',
    alignItems: 'flex-start',
    justifyContent: 'center',
    gap: 4,
    flex: 1,
    flexShrink: 1,
    minWidth: 0,
  },
  // `maxWidth: '100%'` is what makes the title truncate on wide web. The
  // stack is `alignItems: 'flex-start'`, so this row is sized to its content
  // and the title's own `max-width: 100%` (RNW `numberOfLines={1}`) resolved
  // against an unconstrained row; between 1024 and ~1213px a long project
  // name ran under PDF once the three trailing actions shared PDF's line
  // (found in review, 2026-09-09).
  titleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    minWidth: 0,
    maxWidth: '100%',
    flexShrink: 1,
  },
  bandRow: {
    width: '100%',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    // `columnGap`, not `gap`: row 2 adds a `rowGap`, and on web `gap` and
    // `rowGap` compile to two same-specificity classes whose winner depends
    // on insertion order. Separate longhands cannot collide.
    columnGap: spacing.sm,
    minWidth: 0,
  },
  bandRowCompact: {
    alignItems: 'center',
    paddingVertical: REQUEST_TOOLBAR_COMPACT_ROW_PADDING_Y,
  },
  // Row 1 on compact: identity only, and it must not wrap. The title carries
  // `numberOfLines={1}` and `flexShrink: 1`; the stage cluster is `flexShrink: 0`.
  bandRowIdentity: {
    flexWrap: 'nowrap',
  },
  // Row 2 on compact is the only row that wraps. End-justified: with Tags
  // holding the left edge by its auto margin, every other item and every
  // wrapped line sits on the right edge, with or without Tags. Wrapped
  // lines inside the band sit close; the band's own padding is the only air
  // above and below (the four-line toolbar of 2026-09-09 stacked row padding
  // per line).
  bandRowActions: {
    flexWrap: 'wrap',
    justifyContent: 'flex-end',
    rowGap: spacing.xs,
  },
  // Subtle separation between identity row and Tags/PDF row on compact.
  // Border sits on the padded row edge — no extra paddingBottom (that skewed insets).
  bandRowCompactDivider: {
    borderBottomWidth: 1,
    borderBottomColor: colors.borderCard,
  },
  // #1531: compact row 1's identity is a COLUMN — the title line, then the
  // request number under it. Row 1's `flex: 1` + `minWidth: 0` moved from the
  // title row up to this stack, so the title shrinks and truncates exactly as
  // it did before. The inner row stretches instead of flexing: inside a column
  // `flex` is the VERTICAL axis, and growing the title row's height is not what
  // the old `flexGrow: 1` was there to do.
  identityStack: {
    flexDirection: 'column',
    alignItems: 'flex-start',
    justifyContent: 'center',
    gap: 2,
    flex: 1,
    flexShrink: 1,
    minWidth: 0,
  },
  identityStackCompact: {
    flexGrow: 1,
    flexBasis: 'auto',
  },
  identityInline: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    alignSelf: 'stretch',
    flexShrink: 1,
    minWidth: 0,
  },
  title: {
    color: colors.primary,
    fontSize: 16,
    fontWeight: '700',
    flexShrink: 1,
    minWidth: 0,
  },
  // #1531. `textSecondary` (#666 — 5.74:1 on this toolbar's white), NOT the
  // `textMuted` #999 the request list uses for the same id: at 13px #999 is
  // 2.85:1 and fails WCAG AA 1.4.3. This line exists to be read and quoted,
  // so it is the one place that id must not be de-emphasised into unreadable.
  requestNumber: {
    color: colors.textSecondary,
    fontSize: 13,
    fontWeight: '600',
    lineHeight: 16,
    flexShrink: 1,
    minWidth: 0,
    maxWidth: '100%',
  },
  titleAccessory: {
    alignSelf: 'center',
    flexShrink: 0,
  },
  stageRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    flexShrink: 0,
  },
  actionLeadingCluster: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    flexShrink: 0,
  },
  // The one auto margin in compact row 2 (see the render comment). The first
  // item never breaks a line, so Yoga's line-accounting quirk cannot fire.
  actionLeadingCompact: {
    marginRight: 'auto',
  },
  actionCluster: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    justifyContent: 'flex-end',
    flexShrink: 0,
    alignSelf: 'center',
    ...Platform.select({
      web: {
        height: '100%' as const,
      },
      default: {},
    }),
  },
  // Compact row 2: PDF and the trailing group (whatever the screen passes:
  // View Devices alone on Create Request, three buttons on Request Detail)
  // are separate flex items so the wrapping row can keep PDF beside Tags and
  // move only the group down. NO auto margin here (see the render comment).
  // `'auto'`, NOT `undefined`: `actionCluster` sets `height: '100%'` on web
  // and react-native-web drops an `undefined` value from a later style, so
  // the cluster kept 100% of the wrapped row, grew to fill both lines, and
  // pushed the trailing group past the row's computed height, where the
  // content area painted over it (measured in the live page at 660px,
  // 2026-09-09). Jest hides this: RN's flattener treats `undefined` as an
  // override, so a style assertion on `undefined` proves nothing on web.
  actionClusterCompact: {
    height: 'auto',
  },
  // Wide web: the trailing slot is a row like the cluster around it. The
  // screens pass their buttons as a fragment (see `actionTrailing`), so this
  // wrapper owns the direction; a bare View stacked the three buttons
  // vertically beside PDF (found on the web pass, 2026-09-09).
  actionTrailingWide: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
  },
  actionTrailingCompact: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    flexWrap: 'wrap',
    gap: spacing.sm,
    // May shrink to the line and wrap its buttons (320pt) instead of clipping.
    flexShrink: 1,
    minWidth: 0,
  },
  toolbarDivider: {
    width: 1,
    alignSelf: 'stretch',
    minHeight: 24,
    marginVertical: 6,
    backgroundColor: colors.borderCard,
  },
});
